/* eslint-disable max-len */
// eslint-disable-next-line max-len
import AddIcon from '@mui/icons-material/Add';
import TodayIcon from '@mui/icons-material/Today';
import {
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import styled from '@mui/styled-engine';
import { uniqueId } from 'lodash';
import React, { useCallback, useState } from 'react';
import { format } from 'date-fns';

import { useDispatch } from 'react-redux';
import {
  setChooseReturn,
  setDepartDate,
  setReturnDate,
  setShowCalendarVietNam,
} from '@/slices/calendar/calendarSlice';
import { getAreaPointArr } from '@/utils';
import { VectorIcon } from '../../../public/icons';
import CalendarList from '../../components/CalendarList';
import { useAppSelector } from '../../utils/hook';
import ChoosePassenger from './ChoosePassenger';
import ChooseSeatClass from './ChooseSeatClass';
import { LocationSearchType } from './SearchDialog/index.d';
import SearchFormInput from './SearchFormInput';
import {
  TMinFareObj, useLazyGetFlightStatistics2Query,
} from '../../slices/flightData/apis/flightDataApi';
import TransportationTabs from '../../components/TransportationTabs';
import FindRouteButton from './FindRouteButton/index';
import { TSetSelectedDateParams } from '../../components/CustomCalendar/type';
import { addAllHomePoints, adjustAllPassengers } from '../../slices/area/slices/searchFlightFormSlice';
import { IPassengerSearchForm } from '../../slices/passenger/slices/type.d';
import { SeatClassType } from './ChooseSeatClass/type';
import { style } from '@/styles/globals';
import { pr } from '@/utils/pxToRem';
import IOSSwitch from '@/components/IosSwitch';
import { monthYears } from '@/utils/monthYears';
import { TGenerateFlightCountRequest } from '@/utils/generateFlightRequest';
// import { IS_SHOW_CALENDAR_VN } from '@/utils/constants';

const StyledButton = styled(Button)({
  background: style.color.black7,
  display: 'flex',
  height: 46,
  cursor: 'pointer',
  overflow: 'hidden',
  boxSizing: 'border-box',
  justifyContent: 'center',
  alignItems: 'center',
  transitionDelay: '0.1s',
  '&:hover': {
    background: style.color.black7,
  },
});
const GroupInput = styled('div')({
  display: 'flex',
});
export const VectorIconWrapper = styled('div')({
  display: 'inline',
  background: style.color.white,
  borderRadius: '50%',
  position: 'absolute',
  top: '2em',
  right: '2em',
  // boxShadow: style.boxShadow.customShadow,
});
const StyledGridContainer = styled('div')`
  display: block;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  margin-top: 12px;
  margin-bottom: 10px;
`;
const StyledDialog = styled(Dialog)`
  .MuiButtonBase-root.MuiTab-root.Mui-selected {
    color: black;
  }
  .MuiTabs-indicator {
    background: black;
  }
  .MuiPaper-root > .MuiTabs-root {
    height: 5em;
  }
`;
const StyledSelect = styled(Select)`
  div.MuiSelect-select.Mui-disabled {
    -webkit-text-fill-color: ${style.color.black2} !important;
  }
`;

export type TSelectedLocalDate = {
  depart: Date;
  return: Date | null;
};

interface SearchFlightTabProps {
  onSearchClick?: () => void;
  filterAirline?: any;
  isCallFromHomepage?: boolean;
  isVcms?: boolean;
}

const SearchFlightTab: React.FC<SearchFlightTabProps> = ({
  onSearchClick,
  filterAirline,
  isCallFromHomepage,
  isVcms,
}) => {
  const dispatch = useDispatch();
  const stateSelectedDate = useAppSelector(
    (state) => state.calendar.selectedDate,
  );
  const {
    startPointHome,
    toPointHome,
    seatClass: stateSeatClass,
    passenger: statePassenger,
  } = useAppSelector((state) => state.searchFlightForm);
  const startPoint = startPointHome;
  const toPoint = toPointHome;

  const [selectedDepartDate, setSelectedDepartDate] = useState(
    stateSelectedDate && stateSelectedDate.depart ? new Date(stateSelectedDate.depart) : new Date(),
  );
  const [selectedReturnDate, setSelectedReturnDate] = useState(
    stateSelectedDate && stateSelectedDate.return ? new Date(stateSelectedDate.return) : null,
  );
  const [passenger, adjustPassengerState] = useState<IPassengerSearchForm>(statePassenger);
  const [seatClass, adjustSeatClass] = useState<SeatClassType>(stateSeatClass);
  const selectedDate = {
    depart: selectedDepartDate,
    return: selectedReturnDate,
  };

  const [getMinFare, { isLoading, data }] = useLazyGetFlightStatistics2Query();
  const flightRequest: TGenerateFlightCountRequest = {
    from: startPoint ? getAreaPointArr(startPoint) : [],
    to: toPoint ? getAreaPointArr(toPoint) : [],
    month_years: monthYears(),
    flight_class: seatClass,
    quantity: passenger ? (passenger.adt + passenger.chd) : 0,
    infant_count: passenger ? passenger.inf : 0,
    child_infant_count: passenger ? (passenger.chd + passenger.inf) : 0,
    // disable_update_filter: 1,
  };

  const flightRequestReturn: TGenerateFlightCountRequest = {
    from: toPoint ? getAreaPointArr(toPoint) : [],
    to: startPoint ? getAreaPointArr(startPoint) : [],
    month_years: monthYears(),
    flight_class: seatClass,
    quantity: passenger ? (passenger.adt + passenger.chd) : 0,
    child_infant_count: passenger ? (passenger.chd + passenger.inf) : 0,
    infant_count: passenger ? passenger.inf : 0,
    // disable_update_filter: 1,
  };

  if (filterAirline) {
    flightRequest.airline = filterAirline;
  }
  const departDate = selectedDate.depart;
  const returnDate = selectedDate.return ? selectedDate.return : null;
  const [openDialog, setOpenDialog] = React.useState(false);

  const setSelectedDate = (params: TSetSelectedDateParams) => {
    if (params && params.depart !== undefined && params.depart !== selectedDepartDate) {
      if (isCallFromHomepage) {
        dispatch(setDepartDate({ depart: params.depart.toISOString() }));
      }
      setSelectedDepartDate(params.depart);
    }
    if (params.return && params.return !== selectedReturnDate && openDialog) {
      setOpenDialog(false);
    }
    if (
      params.return === null
      || (params.return !== undefined && params.return !== selectedReturnDate)
    ) {
      if (isCallFromHomepage) {
        dispatch(
          setReturnDate({ return: params?.return?.toISOString() || null }),
        );
      }
      setSelectedReturnDate(params.return);
    }
  };

  const renderDay = (dayArr = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']) => {
    const DayComponent = dayArr.map((day) => {
      const isWeekend = /T7|CN/i.test(day);
      return (
        <Typography
          variant="subtitle2"
          key={uniqueId()}
          style={{ color: isWeekend ? style.color.red3 : style.color.black3 }}
        >
          {day}
        </Typography>
      );
    });
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
        }}
      >
        {DayComponent}
      </div>
    );
  };
  const selectDateClick = ({ isReturn = false }: { isReturn?: boolean }) => {
    if (startPoint && toPoint) {
      if (isReturn) {
        getMinFare(flightRequestReturn);
      } else {
        getMinFare(flightRequest);
      }
    }
  };

  const DateDialog = ({ isReturn = false }: { isReturn?: boolean }) => (
    <StyledButton
      // disabled={!(startPoint && toPoint)}
      onClick={() => {
        setOpenDialog(true);
        if (isReturn) {
          dispatch(setChooseReturn({ chooseReturn: true }));
        } else {
          dispatch(setChooseReturn({ chooseReturn: false }));
          setSelectedDate({ return: null });
        }
        // setSelectedDate({ return: null });
      }}
      style={{
        flex: 1,
        [isReturn ? 'marginLeft' : 'marginRight']: pr`4px`,
        textTransform: 'inherit',
        borderRadius: pr`6px`,
      }}
    >
      <div
        style={{
          margin: '0 0.5em 0em 0.5em',
          display: 'flex',
          color: '#4F4F4F',
        }}
      >
        <TodayIcon />
      </div>
      <FormControl
        sx={{ flex: 1 }}
        variant="standard"
      >
        {/* <InputLabel
          style={{ top: pr`8px` }}
          id="departDayDialog"
        >
          {isReturn ? 'Ngày về' : 'Ngày đi'}
        </InputLabel> */}
        <StyledSelect
          style={{
            textAlign: 'left',
            fontWeight: 'bold',
          }}
          value={10}
          fullWidth
          disableUnderline
          open={false}
          disabled
          IconComponent={() => null}
          labelId="departDayDialog"
          onClick={() => {
            setOpenDialog(true);
            if (startPoint && toPoint) {
              if (isReturn) {
                getMinFare(flightRequestReturn);
              } else {
                getMinFare(flightRequest);
              }
            }
          }}
          SelectDisplayProps={{
            style: {
              fontSize: '1rem',
            },
          }}
        >
          <MenuItem value={10}>
            {format(
              isReturn && returnDate ? returnDate : departDate,
              'dd/MM/yyyy',
            )}
          </MenuItem>
        </StyledSelect>
      </FormControl>
    </StyledButton>
  );

  const dayPriceObj = {} as TMinFareObj;
  data?.forEach((group) => {
    Object.keys(group?.grouped_by_date).forEach((groupedDate) => {
      dayPriceObj[groupedDate] = group?.grouped_by_date[groupedDate].min_fare;
    });
  });
  const isShowCalendarVietNam = useAppSelector(
    (state) => state.calendar.showCalendarVietNam,
  );
  // React.useEffect(() => {
  //   // @ts-ignore
  //   const showCalendarVietNam = !!+window.localStorage.getItem(
  //     IS_SHOW_CALENDAR_VN,
  //   );
  //   dispatch(setShowCalendarVietNam({ value: showCalendarVietNam }));
  // }, []);

  const handleAdjustPassenger = useCallback(
    (tempPass: IPassengerSearchForm) => {
      if (isCallFromHomepage) {
        dispatch(adjustAllPassengers(tempPass));
      }
      adjustPassengerState(tempPass);
    },
    [],
  );

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <SearchFormInput type={LocationSearchType.FROM} />
        <SearchFormInput type={LocationSearchType.TO} />
        <VectorIconWrapper>
          <IconButton
            onClick={() => {
              dispatch(
                addAllHomePoints([
                  {
                    data: startPoint,
                    type: 'to',
                  },
                  {
                    data: toPoint,
                    type: 'from',
                  },
                ]),
              );
            }}
            style={{
              width: 40,
              height: 40,
              // boxShadow: "none !important",
              border: `solid  1.5px ${style.color.black6}`,
              // backgroundColor: "#00000000 !important"
            }}
          >
            <VectorIcon color={style.color.product3} />
          </IconButton>
        </VectorIconWrapper>
      </div>

      <GroupInput>
        <DateDialog />
        {selectedDate.return ? (
          <DateDialog isReturn />
        ) : (
          <StyledButton
            // disabled={!(startPoint && toPoint)}
            style={{
              textTransform: 'inherit',
              flex: '1 1 1px',
              marginLeft: pr`4px`,
              borderRadius: pr`6px`,
            }}
            onClick={() => {
              setOpenDialog(true);
              if (startPoint && toPoint) {
                getMinFare(flightRequestReturn);
              }
              dispatch(setChooseReturn({ chooseReturn: true }));
              setSelectedDate({ return: null });
            }}
          >
            <div
              style={{
                marginTop: pr`8px`,
                marginLeft: pr`8px`,
                marginRight: pr`8px`,
                color: '#4F4F4F',
              }}
            >
              <AddIcon fontSize="small" />
            </div>
            <Typography
              style={{
                width: '100%',
                textAlign: 'left',
                fontSize: '1rem',
                fontWeight: 'bold',
              }}
              variant="body2"
              color={style.color.product3}
            >
              Thêm ngày về
              <Typography
                style={{ display: 'inline' }}
                variant="body2"
                color={style.color.black4}
              >
                {' '}
                (Không bắt buộc)
              </Typography>
            </Typography>

          </StyledButton>
        )}
      </GroupInput>

      <GroupInput>
        <ChoosePassenger
          passenger={passenger}
          adjustPassenger={handleAdjustPassenger}
        />
        <ChooseSeatClass
          seatClass={seatClass}
          adjustSeatClass={adjustSeatClass}
        />
      </GroupInput>
      <FindRouteButton
        filterAirline={filterAirline}
        onSearchClick={onSearchClick}
        tmpStartPoint={startPoint}
        tmpToPoint={toPoint}
        tmpSelectedDate={selectedDate}
        tmpPassenger={passenger}
        tmpSeatClass={seatClass}
        isShowCalendarVietNam={isShowCalendarVietNam}
        isVcms={isVcms}
      />

      <StyledDialog
        fullScreen
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <Stack
          sx={{
            zIndex: 10,
            boxShadow: '0px 4px 12px rgb(0 0 0 / 4%)',
          }}
        >
          <TransportationTabs
            title="Chọn ngày"
            onBackClick={() => setOpenDialog(false)}
          />
          <StyledGridContainer>{renderDay()}</StyledGridContainer>
          <FormGroup
            style={{
              display: 'flex',
              textAlign: 'end',
              marginBottom: pr`16px`,
            }}
          >
            <FormControlLabel
              style={{
                justifyContent: 'flex-end',
                marginLeft: '10px',
              }}
              componentsProps={{
                typography: {
                  ...style.text.content.regular,
                  color: style.color.black2,
                },
              }}
              control={(
                <IOSSwitch
                  size="small"
                  color="secondary"
                  defaultChecked={isShowCalendarVietNam}
                />
              )}
              label={(
                <Typography
                  variant="body2"
                  sx={{ marginLeft: '8px' }}
                >
                  Lịch âm
                </Typography>
              )}
              defaultChecked={isShowCalendarVietNam}
              onChange={(value: any) => dispatch(
                setShowCalendarVietNam({ value: value?.target?.checked }),
              )}
            />
          </FormGroup>
        </Stack>
        {isLoading ? (
          <div
            style={{
              width: '100%',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="inherit" />
          </div>
        ) : (
          <CalendarList
            selectDateClick={selectDateClick}
            setOpenDialog={setOpenDialog}
            dayPriceObj={dayPriceObj}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        )}
      </StyledDialog>
    </div>
  );
};
export default SearchFlightTab;
